import { axiosInstance } from "../utils/ApiClient";

// Helper function to parse response and handle "no matches" cases
const parseOrganizationalExpansionResponse = (response) => {
  // Handle string responses like "No ExpansionOrganizational matches the given query."
  if (typeof response === "string") {
    if (
      response.includes("No ExpansionOrganizational matches") ||
      response.includes("matches the given query")
    ) {
      return [];
    }
    console.warn("Unexpected string response:", response);
    return [];
  }

  // Handle array responses
  if (Array.isArray(response)) {
    return response;
  }

  // Handle paginated responses
  if (response && Array.isArray(response.results)) {
    return response.results;
  }

  // Handle single object responses
  if (response && typeof response === "object" && response.id) {
    return [response];
  }

  // Handle empty or null responses
  if (
    !response ||
    (typeof response === "object" && Object.keys(response).length === 0)
  ) {
    return [];
  }

  console.warn("Unexpected API response structure:", response);
  return [];
};

export const expansionOrganizationalService = {
  // Get all organizational expansions for an account
  getOrganizationalExpansions: async (accountId) => {
    try {
      const response = await axiosInstance.get(
        `/accounts/${accountId}/expansion-organizationals/`,
      );
      const parsedData = parseOrganizationalExpansionResponse(response.data);

      // Return in a consistent format that matches pagination expectation
      return {
        results: parsedData,
        count: parsedData.length,
      };
    } catch (error) {
      // Handle 404 or other "no results" errors gracefully
      if (error.response?.status === 404) {
        return {
          results: [],
          count: 0,
        };
      }
      console.error("Error fetching organizational expansions:", error);
      throw error;
    }
  },

  // Create a new organizational expansion
  createOrganizationalExpansion: async (accountId, expansionData) => {
    try {
      const response = await axiosInstance.post(
        `/accounts/${accountId}/expansion-organizationals/`,
        expansionData,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating organizational expansion:", error);
      throw error;
    }
  },

  // Update an existing organizational expansion
  updateOrganizationalExpansion: async (
    accountId,
    expansionId,
    expansionData,
  ) => {
    try {
      const response = await axiosInstance.put(
        `/accounts/${accountId}/expansion-organizationals/${expansionId}/`,
        expansionData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating organizational expansion:", error);
      throw error;
    }
  },

  // Partially update an organizational expansion
  partialUpdateOrganizationalExpansion: async (
    accountId,
    expansionId,
    expansionData,
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/accounts/${accountId}/expansion-organizationals/${expansionId}/`,
        expansionData,
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error partially updating organizational expansion:",
        error,
      );
      throw error;
    }
  },

  // Delete an organizational expansion
  deleteOrganizationalExpansion: async (accountId, expansionId) => {
    try {
      await axiosInstance.delete(
        `/accounts/${accountId}/expansion-organizationals/${expansionId}/`,
      );
      return true;
    } catch (error) {
      console.error("Error deleting organizational expansion:", error);
      throw error;
    }
  },

  // Get a specific organizational expansion
  getOrganizationalExpansion: async (accountId, expansionId) => {
    try {
      const response = await axiosInstance.get(
        `/accounts/${accountId}/expansion-organizationals/${expansionId}/`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching organizational expansion:", error);
      throw error;
    }
  },
};
